import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { searchProducts, Product, SearchResult, getTopLevelCategories, Category } from '../services/api';
import ImageSlider from '../components/ImageSlider';
import './SearchPage.css';

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filters, setFilters] = useState({
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    condition: searchParams.get('condition') || '',
    category: searchParams.get('category') || '',
    sortBy: searchParams.get('sortBy') || 'relevance',
    sortOrder: searchParams.get('sortOrder') || 'desc',
  });
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));

  useEffect(() => {
    loadCategories();
    // Se ci sono parametri di ricerca nell'URL, esegui la ricerca al caricamento
    if (searchParams.get('q')) {
      executeSearch(page);
    }
  }, []);

  const loadCategories = async () => {
    try {
      const cats = await getTopLevelCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const updateURL = (newPage: number, newFilters?: any, newQuery?: string) => {
    const query = newQuery !== undefined ? newQuery : searchQuery;
    const updatedFilters = newFilters || filters;

    const params = new URLSearchParams();

    if (query) params.set('q', query);
    if (newPage > 1) params.set('page', newPage.toString());
    if (updatedFilters.minPrice) params.set('minPrice', updatedFilters.minPrice);
    if (updatedFilters.maxPrice) params.set('maxPrice', updatedFilters.maxPrice);
    if (updatedFilters.condition) params.set('condition', updatedFilters.condition);
    if (updatedFilters.category) params.set('category', updatedFilters.category);
    if (updatedFilters.sortBy !== 'relevance') params.set('sortBy', updatedFilters.sortBy);
    if (updatedFilters.sortOrder !== 'desc') params.set('sortOrder', updatedFilters.sortOrder);

    setSearchParams(params);
  };

  const executeSearch = async (pageNum: number = 1) => {
    setLoading(true);
    setError(null);
    setPage(pageNum);
    updateURL(pageNum);

    try {
      const params: any = {
        q: searchQuery,
        page: pageNum,
        limit: 20,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.minPrice) params.minPrice = parseFloat(filters.minPrice);
      if (filters.maxPrice) params.maxPrice = parseFloat(filters.maxPrice);
      if (filters.condition) params.condition = filters.condition;
      if (filters.category) params.category = filters.category;

      const data = await searchProducts(params);
      setResults(data);
    } catch (err: any) {
      setError(err.message || 'Errore durante la ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    executeSearch(1);
  };

  const handleFilterChange = async (newFilters: any) => {
    setFilters(newFilters);
    updateURL(1, newFilters);
    // Esegui la ricerca con i nuovi filtri
    setLoading(true);
    setError(null);
    setPage(1);

    try {
      const params: any = {
        q: searchQuery,
        page: 1,
        limit: 20,
        sortBy: newFilters.sortBy,
        sortOrder: newFilters.sortOrder,
      };

      if (newFilters.minPrice) params.minPrice = parseFloat(newFilters.minPrice);
      if (newFilters.maxPrice) params.maxPrice = parseFloat(newFilters.maxPrice);
      if (newFilters.condition) params.condition = newFilters.condition;
      if (newFilters.category) params.category = newFilters.category;

      const data = await searchProducts(params);
      setResults(data);
    } catch (err: any) {
      setError(err.message || 'Errore durante la ricerca');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="search-page">
      <div className="container">
        <div className="search-section card">
          <h2>🔍 Ricerca Prodotti eBay</h2>

          <div className="search-bar">
            <input
              type="text"
              className="input search-input"
              placeholder="Cerca prodotti..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-primary" onClick={() => handleSearch()}>
              Cerca
            </button>
          </div>

          <div className="filters">
            <div className="filter-group">
              <label>Prezzo Min (€)</label>
              <input
                type="number"
                className="input"
                placeholder="0"
                value={filters.minPrice}
                onChange={(e) => {
                  const newFilters = { ...filters, minPrice: e.target.value };
                  setFilters(newFilters);
                  handleFilterChange(newFilters);
                }}
              />
            </div>

            <div className="filter-group">
              <label>Prezzo Max (€)</label>
              <input
                type="number"
                className="input"
                placeholder="1000"
                value={filters.maxPrice}
                onChange={(e) => {
                  const newFilters = { ...filters, maxPrice: e.target.value };
                  setFilters(newFilters);
                  handleFilterChange(newFilters);
                }}
              />
            </div>

            <div className="filter-group">
              <label>Condizione</label>
              <select
                className="input"
                value={filters.condition}
                onChange={(e) => {
                  const newFilters = { ...filters, condition: e.target.value };
                  setFilters(newFilters);
                  handleFilterChange(newFilters);
                }}
              >
                <option value="">Tutte</option>
                <option value="NEW">Nuovo</option>
                <option value="USED">Usato</option>
                <option value="REFURBISHED">Ricondizionato</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Categoria</label>
              <select
                className="input"
                value={filters.category}
                onChange={(e) => {
                  const newFilters = { ...filters, category: e.target.value };
                  setFilters(newFilters);
                  handleFilterChange(newFilters);
                }}
              >
                <option value="">Tutte le categorie</option>
                {categories.map((cat) => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Ordina per</label>
              <select
                className="input"
                value={filters.sortBy}
                onChange={(e) => {
                  const newFilters = { ...filters, sortBy: e.target.value };
                  setFilters(newFilters);
                  handleFilterChange(newFilters);
                }}
              >
                <option value="relevance">Rilevanza</option>
                <option value="price">Prezzo</option>
                <option value="date">Data</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Ordine</label>
              <select
                className="input"
                value={filters.sortOrder}
                onChange={(e) => {
                  const newFilters = { ...filters, sortOrder: e.target.value };
                  setFilters(newFilters);
                  handleFilterChange(newFilters);
                }}
              >
                <option value="asc">Crescente</option>
                <option value="desc">Decrescente</option>
              </select>
            </div>
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {loading && (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        )}

        {results && !loading && (
          <>
            <div className="results-info">
              <p>
                Trovati <strong>{results.total}</strong> prodotti
                {searchQuery && ` per "${searchQuery}"`}
              </p>
            </div>

            <div className="products-grid">
              {results.items.map((product) => (
                <div key={product.itemId} className="product-card card">
                  <ImageSlider images={product.imageUrls} title={product.title} />

                  <div className="product-content">
                    <h3 className="product-title">{product.title}</h3>

                    <div className="product-price">
                      {formatPrice(product.price, product.currency)}
                    </div>

                    <div className="product-meta">
                      <span className="badge badge-info">
                        {product.condition}
                      </span>
                      <span className="badge badge-success">
                        {product.feedType}
                      </span>
                    </div>

                    <div className="product-details">
                      <p>
                        <strong>Venditore:</strong> {product.seller.username}
                        {product.seller.feedbackScore && (
                          <span> ({product.seller.feedbackScore}⭐)</span>
                        )}
                      </p>

                      {product.location && (
                        <p>
                          <strong>Località:</strong> {product.location.country}
                        </p>
                      )}

                      {product.shipping && (
                        <p>
                          <strong>Spedizione:</strong>{' '}
                          {formatPrice(product.shipping.cost, product.currency)}
                        </p>
                      )}

                      <p className="product-date">
                        Inserito il {formatDate(product.startTime)}
                      </p>
                    </div>

                    <a
                      href={product.itemUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-block"
                    >
                      Vedi su eBay
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {results.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  disabled={page === 1}
                  onClick={() => executeSearch(page - 1)}
                >
                  ← Precedente
                </button>

                <span className="page-info">
                  Pagina {page} di {results.totalPages}
                </span>

                <button
                  className="btn btn-secondary"
                  disabled={page === results.totalPages}
                  onClick={() => executeSearch(page + 1)}
                >
                  Successiva →
                </button>
              </div>
            )}
          </>
        )}

        {results && results.items.length === 0 && !loading && (
          <div className="no-results card">
            <p>
              Nessun prodotto trovato. Prova a modificare i filtri di ricerca.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
